import type { Database } from "@base-hormonal/database";
import type { StorageService } from "../../../shared/api/storage";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";

export type Exam = Database["public"]["Tables"]["exames"]["Row"];

export const EXAM_PAGE_SIZE = 20;

const EXAM_COLUMNS =
  "id,aluno_id,user_id,tipo,data_exame,valores,resumo_ia,arquivo_asset_id,protocolo_id,created_at";

/** Linha enxuta da linha do tempo: só o que o gráfico de marcadores lê. */
export type ExamTimelineRow = Pick<Exam, "id" | "tipo" | "data_exame" | "created_at" | "valores">;

/** Teto da linha do tempo: cobre anos de acompanhamento sem paginar o gráfico. */
export const EXAM_TIMELINE_LIMIT = 200;

export interface ExamValue {
  nome: string;
  valor: string;
  unidade?: string | null;
  referencia?: string | null;
  classificacao?: "baixo" | "normal" | "alto" | "indeterminado";
}

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

export function examValues(exam: Exam): ExamValue[] {
  const raw: unknown = exam.valores;
  if (!Array.isArray(raw)) return [];
  return (raw as unknown[]).filter(
    (value): value is ExamValue =>
      typeof value === "object" &&
      value !== null &&
      typeof (value as { nome?: unknown }).nome === "string" &&
      typeof (value as { valor?: unknown }).valor === "string",
  );
}

export interface MarkerComparison {
  label: string;
  byExam: Map<string, string>;
}

/** Só marcadores presentes em dois ou mais exames podem ser comparados. */
export function comparableMarkers(exams: readonly Exam[]): MarkerComparison[] {
  const markers = new Map<string, MarkerComparison>();
  for (const exam of exams) {
    for (const value of examValues(exam)) {
      const key = value.nome.trim().toLocaleLowerCase("pt-BR");
      if (key === "") continue;
      const entry = markers.get(key) ?? { label: value.nome, byExam: new Map<string, string>() };
      entry.byExam.set(exam.id, `${value.valor}${value.unidade ? ` ${value.unidade}` : ""}`);
      markers.set(key, entry);
    }
  }
  return [...markers.values()]
    .filter((marker) => marker.byExam.size >= 2)
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

/**
 * Escritor único de exames. O arquivo original vive em bucket privado; a
 * remoção do metadado dispara a remoção compensatória do objeto, e um objeto
 * que resista vira telemetria de órfão — nunca um erro exposto ao usuário.
 */
export function createExamsApi(client: TypedSupabaseClient, storage: StorageService) {
  return {
    async list(profileId: string, page = 0, signal?: AbortSignal): Promise<Exam[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("exames")
          .select(EXAM_COLUMNS)
          .eq("aluno_id", profileId)
          .order("data_exame", { ascending: false, nullsFirst: false })
          .range(page * EXAM_PAGE_SIZE, (page + 1) * EXAM_PAGE_SIZE - 1)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as Exam[];
      });
    },

    /** Exames mais recentes, sem paginação, para a linha do tempo de marcadores. */
    async listForTimeline(profileId: string, signal?: AbortSignal): Promise<ExamTimelineRow[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("exames")
          .select("id,tipo,data_exame,created_at,valores")
          .eq("aluno_id", profileId)
          .order("data_exame", { ascending: false, nullsFirst: false })
          .order("created_at", { ascending: false })
          .limit(EXAM_TIMELINE_LIMIT)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as ExamTimelineRow[];
      });
    },

    /** URL temporária do arquivo original. Expira; nunca torna o objeto público. */
    async fileUrl(exam: Exam, seconds?: number | null): Promise<string | null> {
      if (exam.arquivo_asset_id === null) return null;
      return storage.signedUrl("exames", exam.arquivo_asset_id, seconds);
    },

    async deleteExam(exam: Exam): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("exames")
          .delete()
          .eq("id", exam.id)
          .abortSignal(signal);
        raise(error);
      });
      if (exam.arquivo_asset_id !== null) {
        await storage.removeQuietly("exames", [exam.arquivo_asset_id], "exames");
      }
    },

    async updateSummary(examId: string, resumo: string | null): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("exames")
          .update({ resumo_ia: resumo })
          .eq("id", examId)
          .abortSignal(signal);
        raise(error);
      });
    },
  };
}

export type ExamsApi = ReturnType<typeof createExamsApi>;
